'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import {
  appApi,
  useConnectionStatus,
  type ConnectionWorkspaceSnapshot,
  type PublicConnection,
} from '@/infrastructure/api'
import { useClientRealtime } from '@/infrastructure/websocket'

export type DeskDensity = 'standard' | 'compact'
export type DeskPathState = 'live' | 'connecting' | 'error' | 'offline'
export type DeskRealtimeState = 'on' | 'connecting' | 'error' | 'off'

const DENSITY_STORAGE_KEY = 'mechagora:client-desk:density'
const AUTO_REFRESH_STORAGE_KEY = 'mechagora:client-desk:auto-refresh'
const AUTO_REFRESH_MS = 30_000
const DEFAULT_SYMBOL = 'AAPL'

const CLIENT_DESK_PREFIXES = [
  '/client/dashboard',
  '/market-data',
  '/portfolio',
  '/ai-trading',
  '/trading',
  '/activity',
  '/chat-ai',
] as const

function normalizedEnvironment(connection: PublicConnection): string {
  return connection.environment.trim().toLowerCase()
}

export function isSimulatedConnection(connection: PublicConnection): boolean {
  return (
    normalizedEnvironment(connection) === 'simulated' ||
    connection.capabilities?.simulated === true ||
    connection.metadata?.mode?.trim().toLowerCase() === 'simulated'
  )
}

export function isDeskConnectionAvailable(connection: PublicConnection): boolean {
  return connection.status.trim().toLowerCase() === 'active' || isSimulatedConnection(connection)
}

export function resolveDeskConnectionId(
  connections: PublicConnection[],
  requestedConnectionId: string,
  currentConnectionId: string
): string {
  return (
    connections.find((connection) => connection.connection_id === requestedConnectionId)
      ?.connection_id ||
    connections.find((connection) => connection.connection_id === currentConnectionId)
      ?.connection_id ||
    connections[0]?.connection_id ||
    ''
  )
}

export function mapDeskPathState(
  isConnected: boolean,
  connectionStatus: string,
  hasDataError: boolean
): DeskPathState {
  if (hasDataError && (isConnected || connectionStatus === 'connected')) return 'error'
  if (isConnected || connectionStatus === 'connected') return 'live'
  if (connectionStatus === 'connecting') return 'connecting'
  if (connectionStatus === 'error') return 'error'
  return 'offline'
}

export function mapDeskRealtimeState(
  connected: boolean,
  status: string
): DeskRealtimeState {
  if (connected || status === 'connected') return 'on'
  if (status === 'connecting') return 'connecting'
  if (status === 'error') return 'error'
  return 'off'
}

function normalizeSymbol(value: string | null | undefined): string {
  const symbol = value?.trim().toUpperCase() || ''
  return /^[A-Z0-9._/-]{1,24}$/.test(symbol) ? symbol : ''
}

export interface ClientDeskSessionValue {
  connections: PublicConnection[]
  selectedConnectionId: string
  selectedConnection: PublicConnection | null
  selectConnection: (connectionId: string) => void
  workspace: ConnectionWorkspaceSnapshot | null
  connectionsLoading: boolean
  workspaceLoading: boolean
  connectionsError: Error | null
  workspaceError: Error | null
  lastCheckedAt: number | null
  refresh: (options?: { connections?: boolean }) => Promise<void>
  refreshWorkspace: () => Promise<void>
  path: DeskPathState
  realtime: DeskRealtimeState
  isPathConnected: boolean
  connectionStatus: string
  realtimeStatus: string
  isRealtimeConnected: boolean
  symbol: string
  setSymbol: (symbol: string) => void
  /** Selected-instrument quote time. Trade chrome uses this instead of workspace as-of. */
  quoteAsOf: string | null
  setQuoteAsOf: (value: string | null) => void
  density: DeskDensity
  setDensity: (density: DeskDensity) => void
  autoRefresh: boolean
  setAutoRefresh: (enabled: boolean) => void
  isSimulated: boolean
  isNoConnection: boolean
}

const ClientDeskSessionContext = createContext<ClientDeskSessionValue | undefined>(
  undefined
)

export function ClientDeskSessionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const requestedConnectionId = searchParams.get('connection_id')?.trim() || ''
  const requestedSymbol = normalizeSymbol(searchParams.get('symbol'))
  const enabled = CLIENT_DESK_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
  const { isConnected, connectionStatus } = useConnectionStatus()

  const [connections, setConnections] = useState<PublicConnection[]>([])
  const [selectedConnectionId, setSelectedConnectionId] = useState('')
  const [workspace, setWorkspace] =
    useState<ConnectionWorkspaceSnapshot | null>(null)
  const [connectionsLoading, setConnectionsLoading] = useState(true)
  const [workspaceLoading, setWorkspaceLoading] = useState(false)
  const [connectionsError, setConnectionsError] = useState<Error | null>(null)
  const [workspaceError, setWorkspaceError] = useState<Error | null>(null)
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null)
  const [symbol, setSymbolState] = useState(requestedSymbol || DEFAULT_SYMBOL)
  const [quoteAsOf, setQuoteAsOf] = useState<string | null>(null)
  const [density, setDensityState] = useState<DeskDensity>('standard')
  const [autoRefresh, setAutoRefreshState] = useState(true)

  const selectedIdRef = useRef(selectedConnectionId)
  const requestedIdRef = useRef(requestedConnectionId)
  const connectionsRef = useRef(connections)
  const workspaceRequestRef = useRef<{
    connectionId: string
    promise: Promise<void>
  } | null>(null)
  const connectionsRequestRef = useRef<Promise<{
    connections: PublicConnection[]
    selectedConnectionId: string
  }> | null>(null)

  selectedIdRef.current = selectedConnectionId
  requestedIdRef.current = requestedConnectionId
  connectionsRef.current = connections

  useEffect(() => {
    try {
      setDensityState(
        window.localStorage.getItem(DENSITY_STORAGE_KEY) === 'compact'
          ? 'compact'
          : 'standard'
      )
      setAutoRefreshState(
        window.localStorage.getItem(AUTO_REFRESH_STORAGE_KEY) !== 'false'
      )
    } catch {
      setDensityState('standard')
      setAutoRefreshState(true)
    }
  }, [])

  useEffect(() => {
    if (requestedSymbol) setSymbolState(requestedSymbol)
  }, [requestedSymbol])

  const loadWorkspaceFor = useCallback((connectionId: string): Promise<void> => {
    if (!connectionId) return Promise.resolve()
    if (
      workspaceRequestRef.current?.connectionId === connectionId
    ) {
      return workspaceRequestRef.current.promise
    }

    setWorkspaceLoading(true)
    setWorkspaceError(null)
    const promise = (async () => {
      try {
        const response = await appApi.connections.getWorkspace(connectionId)
        if (!response.data) {
          throw new Error('The connection returned no workspace snapshot')
        }
        if (selectedIdRef.current === connectionId) {
          setWorkspace(response.data)
          setLastCheckedAt(Date.now())
        }
      } catch (error) {
        if (selectedIdRef.current === connectionId) {
          setWorkspaceError(
            error instanceof Error
              ? error
              : new Error('The connection workspace could not be loaded')
          )
        }
      } finally {
        if (workspaceRequestRef.current?.connectionId === connectionId) {
          workspaceRequestRef.current = null
        }
        if (selectedIdRef.current === connectionId) {
          setWorkspaceLoading(false)
        }
      }
    })()

    workspaceRequestRef.current = { connectionId, promise }
    return promise
  }, [])

  const loadConnections = useCallback(() => {
    if (connectionsRequestRef.current) return connectionsRequestRef.current

    setConnectionsLoading(true)
    setConnectionsError(null)
    const promise = (async () => {
      try {
        const response = await appApi.connections.list()
        const available = (response.data || []).filter(isDeskConnectionAvailable)
        const nextConnectionId = resolveDeskConnectionId(
          available,
          requestedIdRef.current,
          selectedIdRef.current
        )
        setConnections(available)
        if (nextConnectionId !== selectedIdRef.current) {
          selectedIdRef.current = nextConnectionId
          setSelectedConnectionId(nextConnectionId)
          setWorkspace(null)
          setWorkspaceError(null)
          setLastCheckedAt(null)
        }
        if (!nextConnectionId) {
          setWorkspace(null)
          setWorkspaceError(null)
          setWorkspaceLoading(false)
          setLastCheckedAt(null)
        }
        return { connections: available, selectedConnectionId: nextConnectionId }
      } catch (error) {
        setConnectionsError(
          error instanceof Error
            ? error
            : new Error('Trading connections could not be loaded')
        )
        return {
          connections: connectionsRef.current,
          selectedConnectionId: selectedIdRef.current,
        }
      } finally {
        setConnectionsLoading(false)
        connectionsRequestRef.current = null
      }
    })()

    connectionsRequestRef.current = promise
    return promise
  }, [])

  const refreshWorkspace = useCallback(async () => {
    const connectionId = selectedIdRef.current
    if (!connectionId) return
    await loadWorkspaceFor(connectionId)
  }, [loadWorkspaceFor])

  const refresh = useCallback(
    async (options?: { connections?: boolean }) => {
      let connectionId = selectedIdRef.current
      if (options?.connections !== false) {
        const result = await loadConnections()
        connectionId = result.selectedConnectionId
      }
      if (connectionId) await loadWorkspaceFor(connectionId)
    },
    [loadConnections, loadWorkspaceFor]
  )

  useEffect(() => {
    if (!enabled) return
    void loadConnections()
  }, [enabled, loadConnections])

  useEffect(() => {
    if (!enabled || !selectedConnectionId) return
    const currentWorkspaceId =
      workspace?.provenance?.connection_id || workspace?.connection?.connection_id
    if (currentWorkspaceId === selectedConnectionId) return
    void loadWorkspaceFor(selectedConnectionId)
  }, [enabled, loadWorkspaceFor, selectedConnectionId, workspace])

  useEffect(() => {
    if (!enabled || !requestedConnectionId) return
    const requested = connections.find(
      (connection) => connection.connection_id === requestedConnectionId
    )
    if (!requested || requested.connection_id === selectedIdRef.current) return
    selectedIdRef.current = requested.connection_id
    setSelectedConnectionId(requested.connection_id)
    setWorkspace(null)
    setWorkspaceError(null)
    setLastCheckedAt(null)
  }, [connections, enabled, requestedConnectionId])

  const realtimeConnection = useClientRealtime({
    enabled: enabled && Boolean(selectedConnectionId),
    channels: ['orders', 'portfolio'],
    onInvalidate: refreshWorkspace,
    invalidateDebounceMs: 500,
  })

  useEffect(() => {
    if (!enabled || !autoRefresh || !selectedConnectionId) return
    const timer = window.setInterval(() => {
      void refreshWorkspace()
    }, AUTO_REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [autoRefresh, enabled, refreshWorkspace, selectedConnectionId])

  const selectConnection = useCallback((connectionId: string) => {
    if (
      connectionId &&
      !connectionsRef.current.some(
        (connection) => connection.connection_id === connectionId
      )
    ) {
      return
    }
    selectedIdRef.current = connectionId
    setSelectedConnectionId(connectionId)
    setWorkspace(null)
    setWorkspaceError(null)
    setLastCheckedAt(null)
  }, [])

  const setSymbol = useCallback((nextSymbol: string) => {
    const normalized = normalizeSymbol(nextSymbol)
    if (normalized) setSymbolState(normalized)
  }, [])

  const setDensity = useCallback((nextDensity: DeskDensity) => {
    setDensityState(nextDensity)
    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, nextDensity)
    } catch {
      // Persistence is best effort; the provider remains the live owner.
    }
  }, [])

  const setAutoRefresh = useCallback((nextAutoRefresh: boolean) => {
    setAutoRefreshState(nextAutoRefresh)
    try {
      window.localStorage.setItem(
        AUTO_REFRESH_STORAGE_KEY,
        nextAutoRefresh ? 'true' : 'false'
      )
    } catch {
      // Persistence is best effort; the provider remains the live owner.
    }
  }, [])

  const selectedConnection = useMemo(
    () =>
      connections.find(
        (connection) => connection.connection_id === selectedConnectionId
      ) || null,
    [connections, selectedConnectionId]
  )
  const hasDataError = Boolean(connectionsError || workspaceError)
  const path = mapDeskPathState(
    isConnected,
    connectionStatus,
    hasDataError
  )
  const realtime = mapDeskRealtimeState(
    realtimeConnection.isRealtimeConnected,
    realtimeConnection.status
  )

  const value = useMemo<ClientDeskSessionValue>(
    () => ({
      connections,
      selectedConnectionId,
      selectedConnection,
      selectConnection,
      workspace,
      connectionsLoading,
      workspaceLoading,
      connectionsError,
      workspaceError,
      lastCheckedAt,
      refresh,
      refreshWorkspace,
      path,
      realtime,
      isPathConnected: isConnected,
      connectionStatus,
      realtimeStatus: realtimeConnection.status,
      isRealtimeConnected: realtimeConnection.isRealtimeConnected,
      symbol,
      setSymbol,
      quoteAsOf,
      setQuoteAsOf,
      density,
      setDensity,
      autoRefresh,
      setAutoRefresh,
      isSimulated: selectedConnection
        ? isSimulatedConnection(selectedConnection)
        : false,
      isNoConnection: !connectionsLoading && connections.length === 0,
    }),
    [
      autoRefresh,
      connectionStatus,
      connections,
      connectionsError,
      connectionsLoading,
      density,
      isConnected,
      lastCheckedAt,
      path,
      realtime,
      realtimeConnection.isRealtimeConnected,
      realtimeConnection.status,
      refresh,
      refreshWorkspace,
      selectConnection,
      selectedConnection,
      selectedConnectionId,
      setAutoRefresh,
      setDensity,
      quoteAsOf,
      setQuoteAsOf,
      setSymbol,
      symbol,
      workspace,
      workspaceError,
      workspaceLoading,
    ]
  )

  return (
    <ClientDeskSessionContext.Provider value={value}>
      {children}
    </ClientDeskSessionContext.Provider>
  )
}

export function useClientDeskSession(): ClientDeskSessionValue {
  const context = useContext(ClientDeskSessionContext)
  if (!context) {
    throw new Error(
      'useClientDeskSession must be used within ClientDeskSessionProvider'
    )
  }
  return context
}

/** Safe for shell chrome outside the desk provider (e.g. sidebar Pulse). */
export function useOptionalClientDeskSession(): ClientDeskSessionValue | null {
  return useContext(ClientDeskSessionContext) ?? null
}
